/**
 * @file PhoneOrderRow.tsx
 * @description Строка порядка колонок на телефоне (phase19.16, партия SB120,
 * этап 1; uiux-sb120-stage1 §2.6): ручка ≡ (@dnd-kit), место, «Колонка N ·
 * подпись», ▲▼ по 56 px на касании (48 с мышью).
 */

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ChevronDown, ChevronUp, GripVertical } from "lucide-react";

import { cn } from "@/lib/utils";

interface Props {
  /** Номер колонки на ПК (1..N) — он же id для перетаскивания. */
  column: number;
  /** Место на телефоне (1..N). */
  place: number;
  caption: string;
  first: boolean;
  last: boolean;
  onUp: () => void;
  onDown: () => void;
}

const ICON_BUTTON = "inline-flex size-14 shrink-0 items-center justify-center rounded-lg text-on-surface-variant outline-none hover:bg-surface-lowest focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40 can-hover:size-12";

export function PhoneOrderRow({ column, place, caption, first, last, onUp, onDown }: Props) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: column });
  const name = `Колонка ${column} · ${caption}`;
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn("flex min-h-14 min-w-0 items-center gap-1 rounded-lg bg-surface-low px-1", isDragging && "relative z-10 opacity-70 shadow-lg")}
    >
      <button type="button" {...attributes} {...listeners} aria-label={`Перетащить: ${name}`} className={cn(ICON_BUTTON, "cursor-grab touch-none")}>
        <GripVertical aria-hidden className="size-4" />
      </button>
      <span className="w-5 shrink-0 text-center text-sm font-medium tabular-nums text-on-surface">{place}</span>
      {/* Две строки: «Колонка N» и подпись — подпись обрезается с конца (QA D9). */}
      <span className="flex min-w-0 flex-1 flex-col" title={name}>
        <span className="type-caption text-on-surface-variant">Колонка {column}</span>
        <span className="type-body truncate text-on-surface">{caption}</span>
      </span>
      <button type="button" className={ICON_BUTTON} disabled={first} onClick={onUp} aria-label={`Выше: ${name}`}>
        <ChevronUp aria-hidden className="size-5" />
      </button>
      <button type="button" className={ICON_BUTTON} disabled={last} onClick={onDown} aria-label={`Ниже: ${name}`}>
        <ChevronDown aria-hidden className="size-5" />
      </button>
    </li>
  );
}
